// Custom ball physics: gravity, quadratic drag, Magnus force from spin, ground bounce/roll,
// goal frame (posts + crossbar), soft box net, advertising boards and goalkeeper colliders.
// Pure module (no rendering), so it is unit-tested directly in Node.
import { Vector3 } from 'three';
import { BALL_R, GOAL, PHYS, BOARD_Z } from './config.js';

const _a = new Vector3();
const _b = new Vector3();
const _c = new Vector3();
const _d = new Vector3();
const _prev = new Vector3();
const UP = new Vector3(0, 1, 0);

export class BallBody {
  constructor() {
    this.pos = new Vector3();
    this.vel = new Vector3();
    this.spin = new Vector3(); // angular velocity, rad/s
    this.onGround = true;
  }
  reset(x, y, z) {
    this.pos.set(x, y, z);
    this.vel.set(0, 0, 0);
    this.spin.set(0, 0, 0);
    this.onGround = true;
  }
}

const PX = GOAL.halfWidth + GOAL.postR; // post centre x
const BAR_Y = GOAL.height + GOAL.postR; // crossbar centre y

/** Static world description. `full: false` gives ground only (used for trajectory prediction). */
export function createWorld({ full = true } = {}) {
  if (!full) return { segments: [], panels: [], keeper: null };
  return {
    segments: [
      { a: new Vector3(-PX, 0, 0), b: new Vector3(-PX, BAR_Y, 0), r: GOAL.postR, kind: 'post' },
      { a: new Vector3(PX, 0, 0), b: new Vector3(PX, BAR_Y, 0), r: GOAL.postR, kind: 'post' },
      { a: new Vector3(-PX, BAR_Y, 0), b: new Vector3(PX, BAR_Y, 0), r: GOAL.postR, kind: 'bar' },
    ],
    // Axis-aligned rectangles. `axis` is the normal axis; `u`/`v` give bounds on the other two axes.
    panels: [
      { id: 'back', axis: 'z', at: -GOAL.depth, u: ['x', -PX, PX], v: ['y', 0, BAR_Y], net: true },
      { id: 'roof', axis: 'y', at: BAR_Y, u: ['x', -PX, PX], v: ['z', -GOAL.depth, 0], net: true },
      { id: 'left', axis: 'x', at: -PX, u: ['z', -GOAL.depth, 0], v: ['y', 0, BAR_Y], net: true },
      { id: 'right', axis: 'x', at: PX, u: ['z', -GOAL.depth, 0], v: ['y', 0, BAR_Y], net: true },
      { id: 'boards', axis: 'z', at: BOARD_Z, u: ['x', -40, 40], v: ['y', 0, 1.0], net: false },
    ],
    keeper: null, // array of { pos: Vector3, r, kind } supplied by the goalkeeper each step
  };
}

function closestOnSegment(p, a, b, out) {
  _c.subVectors(b, a);
  const t = Math.max(0, Math.min(1, _d.subVectors(p, a).dot(_c) / _c.lengthSq()));
  return out.copy(a).addScaledVector(_c, t);
}

/** Sphere-vs-sphere style response. Returns the normal impact speed (>0) when a collision happened. */
function collideSphere(ball, center, radius, restitution, tangentKeep) {
  _a.subVectors(ball.pos, center);
  const minDist = BALL_R + radius;
  const distSq = _a.lengthSq();
  if (distSq >= minDist * minDist) return 0;
  const dist = Math.sqrt(distSq) || 1e-6;
  _a.multiplyScalar(1 / dist); // normal
  ball.pos.copy(center).addScaledVector(_a, minDist);
  const vn = ball.vel.dot(_a);
  if (vn >= 0) return 0;
  // split velocity into normal and tangential parts
  _b.copy(_a).multiplyScalar(vn);
  ball.vel.sub(_b).multiplyScalar(tangentKeep).addScaledVector(_b, -restitution);
  ball.spin.multiplyScalar(0.6);
  return -vn;
}

/**
 * Advance the ball by dt seconds. `events` (optional array) receives impact events:
 * { type: 'ground'|'post'|'bar'|'net'|'boards'|'keeper', speed, panel?, collider?, point }
 */
export function stepBall(ball, dt, world, events) {
  _prev.copy(ball.pos);
  const v = ball.vel;
  const speed = v.length();

  // forces -> acceleration
  _a.set(0, -PHYS.gravity, 0);
  _a.addScaledVector(v, -PHYS.drag * speed);
  if (!ball.onGround) {
    _b.crossVectors(ball.spin, v).multiplyScalar(PHYS.magnus);
    _a.add(_b);
  }
  v.addScaledVector(_a, dt);
  ball.pos.addScaledVector(v, dt);
  ball.spin.multiplyScalar(Math.exp(-PHYS.spinDecay * dt));

  // ground
  if (ball.pos.y < BALL_R) {
    ball.pos.y = BALL_R;
    if (v.y < 0) {
      const impact = -v.y;
      if (impact > 0.7) {
        v.y = impact * PHYS.groundRestitution;
        v.x *= 1 - PHYS.groundFriction;
        v.z *= 1 - PHYS.groundFriction;
        ball.spin.multiplyScalar(0.5);
        ball.onGround = false;
        events?.push({ type: 'ground', speed: impact, point: ball.pos.clone() });
      } else {
        v.y = 0;
        ball.onGround = true;
      }
    }
  } else if (ball.pos.y > BALL_R + 0.01) {
    ball.onGround = false;
  }
  if (ball.onGround) {
    const hs = Math.hypot(v.x, v.z);
    if (hs > 0) {
      const drape = ball.pos.z < 0 && world ? 5 : 1; // loose net lying on the grass inside the goal
      const ns = Math.max(0, hs - PHYS.rollResistance * drape * dt);
      v.x *= ns / hs;
      v.z *= ns / hs;
    }
    // rolling without slipping: spin = (up x v) / r
    ball.spin.crossVectors(UP, v).multiplyScalar(1 / BALL_R);
  }

  if (!world) return;

  // goal frame
  for (const s of world.segments) {
    closestOnSegment(ball.pos, s.a, s.b, _d);
    const hit = collideSphere(ball, _d.clone(), s.r, PHYS.postRestitution, 0.9);
    if (hit > 0.2) events?.push({ type: s.kind, speed: hit, point: ball.pos.clone() });
  }

  // net panels and boards
  for (const p of world.panels) {
    const [ua, umin, umax] = p.u;
    const [va, vmin, vmax] = p.v;
    const pu = ball.pos[ua];
    const pv = ball.pos[va];
    if (pu < umin || pu > umax || pv < vmin || pv > vmax) continue;
    const side = Math.sign(_prev[p.axis] - p.at) || 1;
    const d = ball.pos[p.axis] - p.at;
    if (Math.sign(d) === side && Math.abs(d) >= BALL_R) continue;
    ball.pos[p.axis] = p.at + side * BALL_R;
    const vn = v[p.axis] * side;
    if (vn < 0) {
      const keep = p.net ? PHYS.netAbsorb : 0.35;
      const tangent = p.net ? PHYS.netTangent : 0.8;
      const normalSpeed = -vn;
      for (const ax of ['x', 'y', 'z']) v[ax] *= tangent;
      v[p.axis] = side * normalSpeed * keep;
      ball.spin.multiplyScalar(0.5);
      if (normalSpeed > 0.4) {
        events?.push({ type: p.net ? 'net' : 'boards', panel: p.id, speed: normalSpeed, side, point: ball.pos.clone() });
      }
    }
  }

  // goalkeeper body parts
  if (world.keeper) {
    for (const c of world.keeper) {
      const glove = c.kind === 'glove';
      const hit = collideSphere(ball, c.pos, c.r, glove ? 0.42 : 0.3, glove ? 0.55 : 0.7);
      if (hit > 0) events?.push({ type: 'keeper', collider: c.kind, speed: hit, point: ball.pos.clone() });
    }
  }
}

const _sim = new BallBody();
const _groundWorld = createWorld({ full: false });

/**
 * Fly a ball (ground only, no goal) until it crosses the plane z = planeZ.
 * Returns { x, y, t } at the crossing, or null if it never gets there within maxT.
 * When `path` is an array, sampled positions are appended to it.
 */
export function simulateCrossing(from, vel, spin, { planeZ = 0, dt = 1 / 120, maxT = 3, path = null, sampleEvery = 3 } = {}) {
  _sim.pos.copy(from);
  _sim.vel.copy(vel);
  _sim.spin.copy(spin);
  _sim.onGround = from.y <= BALL_R + 1e-3 && vel.y <= 0;
  let t = 0;
  let i = 0;
  while (t < maxT) {
    const pz = _sim.pos.z;
    const px = _sim.pos.x;
    const py = _sim.pos.y;
    stepBall(_sim, dt, _groundWorld, null);
    t += dt;
    if (path && i++ % sampleEvery === 0) path.push(_sim.pos.clone());
    if (_sim.pos.z <= planeZ) {
      const f = (pz - planeZ) / (pz - _sim.pos.z || 1);
      return { x: px + (_sim.pos.x - px) * f, y: py + (_sim.pos.y - py) * f, t: t - dt + dt * f };
    }
    if (_sim.vel.z >= 0 && _sim.vel.lengthSq() < 0.01) return null;
  }
  return null;
}

/**
 * Find the launch velocity (fixed speed) that sends a ball from `from` through the aim point
 * (ax, ay) on the goal-line plane, accounting for gravity, drag and spin. Returns
 * { vel, crossing } where crossing is the simulated arrival { x, y, t }.
 */
export function solveShot(from, ax, ay, speed, spin, iterations = 10) {
  const dist = Math.hypot(ax - from.x, from.z);
  let yaw = Math.atan2(ax - from.x, from.z);
  const t0 = dist / speed;
  let pitch = Math.atan2(ay - from.y + 0.5 * PHYS.gravity * t0 * t0 * 1.15, dist);
  const vel = new Vector3();
  let crossing = null;
  for (let i = 0; i < iterations; i++) {
    vel.set(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch)).multiplyScalar(speed);
    crossing = simulateCrossing(from, vel, spin);
    if (!crossing) {
      pitch += 0.05;
      continue;
    }
    const ex = ax - crossing.x;
    const ey = ay - crossing.y;
    if (Math.abs(ex) < 0.005 && Math.abs(ey) < 0.005) break;
    yaw += (ex / dist) * 0.95;
    pitch += (ey / dist) * 0.95;
    pitch = Math.max(-0.2, Math.min(0.9, pitch));
  }
  return { vel, crossing };
}

/** Spin vector for a curve amount in [-1, 1] (positive bends right) plus a little backspin. */
export function spinForCurve(curve, out = new Vector3()) {
  return out.set(9, -curve * 55, 0);
}
